
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { registerUser } from '@/contexts/auth/services/registerService';
import { PersonName, RegisterUserRequest } from '@/contexts/auth/types';
import { UserRoles } from '@/contexts/auth/enums';

export function useRegistrationHandler() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const register = useCallback(async (
    name: PersonName,
    email: string,
    password: string,
    companyName?: string,
    requestedRole: UserRoles = UserRoles.USER
  ) => {
    try {
      const userData: RegisterUserRequest = {
        email,
        plainPassword: password,
        requestedRole,
        site: 'medihub',
        userProfile: {
          name,
          companyInfo: {
            name: companyName ?? '',
          },
        }
      };

      const result = await registerUser(userData);

      if (result.success) {
        // Navigate to success page with email state
        navigate('/registration-success', {
          state: { email }
        });
        return { success: true };
      }

      // Handle API error messages
      if (result.message) {
        // If we have field-specific errors
        if (result.errors) {
          return {
            success: false,
            message: result.message,
            errors: result.errors
          };
        } else {
          toast({
            title: "Regisztrációs hiba",
            description: result.message,
            variant: "destructive",
          });
          return {
            success: false,
            message: result.message
          };
        }
      }

      return {
        success: false,
        message: "Ismeretlen hiba történt"
      };
    } catch (error) {
      console.error('Registration error:', error);

      toast({
        title: "Regisztrációs hiba",
        description: "Váratlan hiba történt a regisztráció során.",
        variant: "destructive",
      });

      return {
        success: false,
        message: 'Váratlan hiba történt a regisztráció során.'
      };
    }
  }, [navigate, toast]);

  return { register };
}
